



























































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Draggable from 'vuedraggable';
import shortid from 'shortid';
import { FlowViewModel, InputVariableViewModel, StateViewModel } from '@/features/bots/models/designer';
import TransitionPicker from './TransitionPicker.vue';
import IslandBadge from './IslandBadge.vue';
import AdditionalInformationWrapper from './AdditionalInformationWrapper.vue';
import InputValidationConfig from './InputValidationConfig.vue';
import { mixin as clickaway } from 'vue-clickaway';
import VariablePicker from './VariablePicker/VariablePicker.vue';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import ChevronRight from 'vue-material-design-icons/ChevronDoubleRight.vue';
import ChevronLeft from 'vue-material-design-icons/ChevronDoubleLeft.vue';
import ErrorIcon from 'vue-material-design-icons/AlertCircleOutline.vue';
import AddIcon from 'vue-material-design-icons/PlusCircle.vue';
import TextEditor from '@/features/bots/components/designer/TextEditor/TextEditor.vue';
import { TransitionViewModel } from '@/features/bots/models/designer/transitionViewModel';
import { RegexType } from '../../models/designer/regexType';
import { Action, Getter } from 'vuex-class';
import { FlowStatus } from '@/features/bots/models/flowStatus';
import { LOAD_FLOWS } from '@/store/bot-designer/actionTypes';
import { FlowListItemViewModel } from '@/features/bots/models/flowListItemViewModel';

@Component({
	mixins: [
		clickaway,
		validationMixin
	],
	components: {
		Draggable,
		IslandBadge,
		AdditionalInformationWrapper,
		InputValidationConfig,
		VariablePicker,
		ChevronRight,
		ChevronLeft,
		ErrorIcon,
		AddIcon,
		TextEditor,
		TransitionPicker
	},
	validations: {
		storedVariable: {
			attributeMissing(value) {
				const inputStringComponent = (this as any); // Causes build error (fine in IDE) if we call this.? directly, work around
				const storedVar: InputVariableViewModel = inputStringComponent.state.storedVariable;
				return !storedVar || storedVar.scope !== 'appUser.customAttributes' || inputStringComponent.doesAttributeExist(storedVar.key);
			},
			attributeArchived(value) {
				const inputStringComponent = (this as any);
				return !inputStringComponent.isArchived();
			}
		},
		state: {
			text: {
				required
			},
			options: {
				required,
				$each: {
					text: {
						required,
						isUnique(value) {
							return !(this.$v.state.options && this.$v.state.options.$model.filter((option: any) => option.text === value).length > 1);
						},
						matchesType(value) {
							if (this.$v.state.$model.pattern) {
								return !!value.match(this.$v.state.$model.pattern);
							}
							return !!value;
						}
					}
				}
			}
		},
		transitions: {
			$each: {
				isValidTransition(transition: TransitionViewModel) {
					const quickReplyComponent = (this as any);
					return quickReplyComponent.isValidTransition(transition);
				},
				required,
				type: {
					required
				},
				payload: {
					payloadValid(payload: string) {
						const component = (this as any);
						if (payload) {
							return !component.isTransitionPayloadInvalid(payload);
						}
						return true;
					}
				}
			}
		}
	}
})
export default class QuickReplyComponent extends Vue {
	@Prop() public value: StateViewModel;
	@Getter public doesAttributeExist: (attributeId: string) => boolean;
	@Getter public isAttributeArchived!: (attributeId: string) => boolean;
	@Getter public isTransitionPayloadInvalid: (payload: string) => boolean;
	@Getter public currentFlow: FlowViewModel;
	@Action(LOAD_FLOWS) public loadFlows: any;
	public showConfig: boolean = false;
	public hasClicked: boolean = false;
	public transition: string = 'display';
	public flows: FlowListItemViewModel[] = [];

	public $refs: {
		textEditor: TextEditor
	};

	public state: StateViewModel = {
		id: '',
		component: 'InputString',
		allowedAttempts: 1,
		options: []
	};

	public transitions: TransitionViewModel[] = [];
	private additionalConfigInvalid = false;

	public async mounted() {
		this.flows = await this.loadFlows();
	}

	@Watch('value', { immediate: true })
	public onValueChanged(value: StateViewModel, previous: StateViewModel) {
		this.state = value;
		if (this.state.options) {
			this.transitions = this.state.options.map(o => o.transition || {});
		}
		this.updateValidation();
	}

	public textUpdated(text: string) {
		if (this.state.text === text) {
			return;
		}

		this.state.text = text;

		if (this.$v.state && this.$v.state.text) {
			this.$v.state.text.$touch();
		}

		this.updateState();
	}

	public updateOptionTransition(index: number, updatedOptionTransition: TransitionViewModel) {
		if (!this.state.options) {
			return;
		}

		if (this.state.options[index]) {
			this.state.options[index].transition = updatedOptionTransition;
		}
		this.transitions = this.state.options.map(o => o.transition || {});
		this.updateState();
	}

	public addOption() {
		if (!this.state.options) {
			this.state.options = [];
		}

		if (!this.canAddMoreOptions()) {
			return;
		}

		this.state.options.push({
			id: `opt-${shortid.generate()}`,
			text: ''
		});

		this.transitions = this.state.options.map(o => o.transition || {});

		this.updateState();
	}

	public deleteOption(optionIndex: number) {
		if (!this.state.options) {
			return;
		}
		this.state.options.splice(optionIndex, 1);
		this.transitions = this.state.options.map(o => o.transition || {});

		this.updateState();
	}

	public setClickedState() {
		this.hasClicked = true;
	}

	public setUnclickedState() {
		this.hasClicked = false;
		this.showConfig = false;
		this.transition = 'display';

		this.$v.$touch();
	}

	public showExpandedConfig() {
		this.hasClicked = true;
		this.showConfig = true;
		this.transition = 'none';
	}

	public hideExpandedConfig() {
		this.showConfig = false;
	}

	public optionUpdated(optionIndex: number, text: string) {
		if (!this.state.options) {
			return;
		}

		if (!this.stateOptionHasBeenUpdated(optionIndex, text)) {
			return;
		}

		this.state.options[optionIndex].text = text;
		this.transitions = this.state.options.map(o => o.transition || {});
		this.updateState();
	}

	public additionalConfigChanged(update: any) {
		this.state = update.state;
		this.additionalConfigInvalid = update.invalid;
		this.updateState();
	}

	public variableSelected(variable: InputVariableViewModel) {
		this.state.storedVariable = variable;
		if (this.state.storedVariable.dataType === 'decimal') {
			this.state.pattern = RegexType.DECIMAL;
		} else if (this.state.storedVariable.dataType === 'number') {
			this.state.pattern = RegexType.NUMERIC;
		} else if (this.isAppUserPhoneNumber()) {
			this.state.pattern = RegexType.PHONE_NUMBER;
		} else if (this.isAppUserEmail()) {
			this.state.pattern = RegexType.EMAIL;
		} else {
			this.state.pattern = '';
		}
		this.state.allowedAttempts = 1;
		this.state.errorMessage = '';

		this.updateState();
	}

	public canAddMoreOptions() {
		return this.state.options && this.state.options.length < 10;
	}

	public getAddTooltipText() {
		if (!this.canAddMoreOptions()) {
			return 'You can only display a maximum of 10 options';
		}
		return '';
	}

	public getDataTypeErrorMessage() {
		if (this.state && this.state.storedVariable && this.state.storedVariable.dataType) {
			if (this.state.storedVariable.dataType === 'decimal') {
				return 'decimal to 2DP';
			} else if (this.isAppUserPhoneNumber()) {
				return 'phone number';
			} else if (this.isAppUserEmail()) {
				return 'email';
			}
			return this.state.storedVariable.dataType;
		}
		return '';
	}

	public isValidTransition(transition: TransitionViewModel): boolean {
		if (this.currentFlow.status === FlowStatus.Live) {
				const flow = this.flows.find(f => f.id === transition.payload);
				if (flow && flow.status !== FlowStatus.Live) {
					return false;
				}
		}
		return true;
	}

	public isArchived(): boolean {
		if (this.state && this.state.storedVariable) {
			return this.isAttributeArchived(this.state.storedVariable!.key);
		}
		return false;
	}

	public focusOnInput() {
		this.$refs.textEditor.focusElement();
		this.$refs.textEditor.startEditing();
	}

	public hasDataType(): boolean {
		return !!(this.state && this.state.storedVariable && this.state.storedVariable.dataType);
	}

	private stateOptionHasBeenUpdated(optionIndex: number, value: string) {
		if (this.state && this.state.options && this.state.options[optionIndex]) {
			return this.state.options[optionIndex].text !== value;
		}
		return false;
	}

	private updateState() {
		this.updateValidation();
		this.$emit('state-updated', this.state);
	}

	private updateValidation() {
		this.state.invalid = this.additionalConfigInvalid || this.$v.$invalid;
	}

	private isAppUserPhoneNumber() {
		return this.state.storedVariable && this.state.storedVariable.scope === 'appUser' && this.state.storedVariable.key === 'phoneNumber';
	}

	private isAppUserEmail() {
		return this.state.storedVariable && this.state.storedVariable.scope === 'appUser' && this.state.storedVariable.key === 'email';
	}
}
